import type { FogBugzCase } from "@/hooks/queries/use-fogbugz";

export const UNCATEGORIZED = "Uncategorized";

/**
 * Groups an array of FogBugzCase items into a nested Map:
 *   project name → area name → cases (sorted by dtLastUpdated descending)
 *
 * - Cases with missing/empty sProject are grouped under 'Uncategorized'.
 * - Cases with missing/empty sArea are grouped under 'Uncategorized' within
 *   their project.
 * - Projects are sorted alphabetically, with 'Uncategorized' last.
 * - Cases within each area group are sorted by dtLastUpdated descending (most recent first).
 */
export function groupCasesByProjectAndArea(
  cases: FogBugzCase[]
): Map<string, Map<string, FogBugzCase[]>> {
  const raw = new Map<string, Map<string, FogBugzCase[]>>();

  for (const c of cases) {
    const project = c.sProject?.trim() || UNCATEGORIZED;
    const area = c.sArea?.trim() || UNCATEGORIZED;

    if (!raw.has(project)) {
      raw.set(project, new Map());
    }
    const areaMap = raw.get(project)!;

    if (!areaMap.has(area)) {
      areaMap.set(area, []);
    }
    areaMap.get(area)!.push(c);
  }

  // Sort projects alphabetically, 'Uncategorized' last
  const sortedProjects = [...raw.keys()].sort((a, b) => {
    if (a === UNCATEGORIZED) return 1;
    if (b === UNCATEGORIZED) return -1;
    return a.localeCompare(b);
  });

  const result = new Map<string, Map<string, FogBugzCase[]>>();
  for (const project of sortedProjects) {
    const areaMap = raw.get(project)!;

    // Sort areas alphabetically, 'Uncategorized' last
    const sortedAreas = [...areaMap.keys()].sort((a, b) => {
      if (a === UNCATEGORIZED) return 1;
      if (b === UNCATEGORIZED) return -1;
      return a.localeCompare(b);
    });

    const sortedAreaMap = new Map<string, FogBugzCase[]>();
    for (const area of sortedAreas) {
      // Sort cases within each area by dtLastUpdated descending.
      // Treat missing OR unparseable timestamps as "oldest" (place last) so the
      // comparator never returns NaN (which would make Array.prototype.sort
      // behave unstably).
      const sorted = [...areaMap.get(area)!].sort((a, b) => {
        const at = a.dtLastUpdated ? Date.parse(a.dtLastUpdated) : NaN;
        const bt = b.dtLastUpdated ? Date.parse(b.dtLastUpdated) : NaN;
        const aValid = Number.isFinite(at);
        const bValid = Number.isFinite(bt);
        if (!aValid && !bValid) return 0;
        if (!aValid) return 1;
        if (!bValid) return -1;
        return bt - at;
      });
      sortedAreaMap.set(area, sorted);
    }

    result.set(project, sortedAreaMap);
  }

  return result;
}

/**
 * Returns a Map of project name → total case count for use in filter bars.
 *
 * Entries are returned in the same order as the grouped view: alphabetical
 * by project name, with 'Uncategorized' last. This keeps the project filter
 * bar visually consistent with the grouped sections.
 */
export function getProjectCounts(cases: FogBugzCase[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const c of cases) {
    const project = c.sProject?.trim() || UNCATEGORIZED;
    counts.set(project, (counts.get(project) ?? 0) + 1);
  }
  const sorted = [...counts.keys()].sort((a, b) => {
    if (a === UNCATEGORIZED) return 1;
    if (b === UNCATEGORIZED) return -1;
    return a.localeCompare(b);
  });
  return new Map(sorted.map((p) => [p, counts.get(p)!]));
}
